import type { KeyboardEvent } from 'react';

export type MentionTreeNode = {
  key: string;
  data: {
    propertyPath: string;
    displayName: string;
    value?: string | unknown;
    isSlice?: boolean;
    insertable: boolean;
    isTestStepNode?: boolean;
    breadcrumb?: string;
  };
  children?: MentionTreeNode[];
};

type HandleStepOutputProps = {
  stepOutput: unknown;
  propertyPath: string;
  displayName: string;
  insertable?: boolean;
};

function traverseStepOutputAndReturnMentionTree({
  stepOutput,
  propertyPath,
  displayName,
  insertable = true,
}: HandleStepOutputProps): MentionTreeNode {
  if (Array.isArray(stepOutput)) {
    return handlingArrayStepOutput(
      stepOutput,
      propertyPath,
      displayName,
      insertable,
    );
  }
  const isObject = stepOutput && typeof stepOutput === 'object';
  if (isObject) {
    return handleObjectStepOutput(
      propertyPath,
      displayName,
      stepOutput,
      insertable,
    );
  }
  return {
    key: propertyPath,
    data: {
      propertyPath,
      displayName,
      insertable,
      value: stepOutput,
    },
    children: undefined,
  };
}

function handlingArrayStepOutput(
  stepOutput: unknown[],
  path: string,
  parentDisplayName: string,
  insertable: boolean,
  startingIndex = 0,
): MentionTreeNode {
  const maxSliceLength = 100;
  const isEmptyList = Object.keys(stepOutput).length === 0;
  if (stepOutput.length <= maxSliceLength) {
    return {
      key: parentDisplayName,
      children: stepOutput.map((output, idx) =>
        traverseStepOutputAndReturnMentionTree({
          stepOutput: output,
          propertyPath: `${path}[${idx + startingIndex}]`,
          displayName: `items [${idx + startingIndex + 1}]`,
        }),
      ),
      data: {
        propertyPath: path,
        displayName: parentDisplayName,
        value: isEmptyList ? 'Empty List' : undefined,
        insertable,
      },
    };
  }

  const numberOfSlices = new Array(
    Math.ceil(stepOutput.length / maxSliceLength),
  ).fill(0);
  const children: MentionTreeNode[] = numberOfSlices.map((_, idx) => {
    const startingIndex = idx * maxSliceLength;
    const endingIndex =
      Math.min((idx + 1) * maxSliceLength, stepOutput.length) - 1;
    const displayName = `items ${startingIndex}-${endingIndex}`;
    const sliceOutput = handlingArrayStepOutput(
      stepOutput.slice(startingIndex, endingIndex),
      path,
      parentDisplayName,
      insertable,
      startingIndex,
    );
    return {
      ...sliceOutput,
      key: displayName,
      data: {
        ...sliceOutput.data,
        displayName,
        isSlice: true,
      },
    };
  });

  return {
    key: parentDisplayName,
    data: {
      propertyPath: path,
      displayName: parentDisplayName,
      value: stepOutput,
      isSlice: false,
      insertable,
    },
    children: children,
  };
}

function handleObjectStepOutput(
  propertyPath: string,
  displayName: string,
  stepOutput: object,
  insertable: boolean,
): MentionTreeNode {
  const isEmptyList = Object.keys(stepOutput).length === 0;
  return {
    key: propertyPath,
    data: {
      propertyPath: propertyPath,
      displayName: displayName,
      value: isEmptyList ? 'Empty List' : undefined,
      insertable,
    },
    children: Object.keys(stepOutput).map((childPropertyKey) => {
      const escapedKey = childPropertyKey.replaceAll(
        /[\\"'\n\r\t’]/g,
        (char) => `\\${char}`,
      );
      return traverseStepOutputAndReturnMentionTree({
        stepOutput: (stepOutput as Record<string, unknown>)[childPropertyKey],
        propertyPath: `${propertyPath}['${escapedKey}']`,
        displayName: childPropertyKey,
      });
    }),
  };
}

export const dataSelectorUtils = {
  traverseStepOutputAndReturnMentionTree,
};

export function calculatePaddingFromDepth(depth: number): number {
  const indentPerDepth = 14;
  return depth * indentPerDepth + 12;
}

// Structural wrapper segments stripped from breadcrumbs (unless they are the
// leaf itself): the webhook envelope and the Kizeo field "result" wrapper.
const BREADCRUMB_NOISE_SEGMENTS = ['body', 'data', 'result'];

export function flattenAndFilterMentionTree(
  nodes: MentionTreeNode[],
  isRoot = true,
  parentNames: string[] = [],
): MentionTreeNode[] {
  if (!nodes) return [];

  return nodes.reduce((acc: MentionTreeNode[], node: MentionTreeNode) => {
    if (
      isRoot &&
      !node.key.includes("['data']") &&
      !node.key.includes("['body']")
    ) {
      return acc;
    }

    const trail = [...parentNames, node.data.displayName];

    if (!node.children || node.children.length === 0) {
      const { value } = node.data;
      if (value === null || value === undefined || value === '') {
        return acc;
      }
      // keep the path as a breadcrumb so each flattened leaf stays
      // unambiguous on large objects (where bare leaf names like "value" or
      // "result" repeat everywhere). Structural wrapper segments (the webhook
      // envelope "body"/"data" and the Kizeo "result" wrapper) are dropped as
      // pure noise — but never the leaf itself, so a field literally named
      // "result" still shows.
      const breadcrumb = trail
        .filter(
          (name, idx) =>
            idx === trail.length - 1 ||
            !BREADCRUMB_NOISE_SEGMENTS.includes(name),
        )
        .join(' › ');
      acc.push({
        key: node.key,
        data: { ...node.data, breadcrumb },
      });
    } else {
      acc.push(...flattenAndFilterMentionTree(node.children, false, trail));
    }

    return acc;
  }, []);
}

export type NodeValueType =
  | 'string'
  | 'number'
  | 'boolean'
  | 'object'
  | 'array'
  | 'undefined';

export function getNodeValueType(node: MentionTreeNode): NodeValueType {
  if (node.children?.length === 0) {
    return 'undefined';
  }

  if (node.children && node.children.length > 0) {
    const hasArrayItems = node.children[0]?.data?.displayName?.includes('[');
    return hasArrayItems ? 'array' : 'object';
  }

  const { value } = node.data;
  if (value === null || value === undefined) {
    return 'undefined';
  }

  return typeof value as NodeValueType;
}

const VALUE_PREVIEW_MAX_LENGTH = 60;

// Short, single-line preview of a value, shown in both the tree and simplified
// views. Nil values yield an empty string — without the guard,
export const formatValuePreview = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    const trimmed = value.replace(/\s+/g, ' ').trim();
    return trimmed.length > VALUE_PREVIEW_MAX_LENGTH
      ? `${trimmed.slice(0, VALUE_PREVIEW_MAX_LENGTH)}…`
      : trimmed;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  const json = JSON.stringify(value);
  return json.length > VALUE_PREVIEW_MAX_LENGTH
    ? `${json.slice(0, VALUE_PREVIEW_MAX_LENGTH)}…`
    : json;
};

// Full, untruncated value for tooltips. Objects are serialized as JSON instead
// of the default '[object Object]' stringification.
export const formatValueTooltip = (value: unknown): string =>
  typeof value === 'string' ? value : JSON.stringify(value) ?? '';

// Lets a focusable row/card behave like a button (Enter/Space → click).
export const triggerClickOnEnterOrSpace = (
  event: KeyboardEvent<HTMLDivElement>,
): void => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    if (event.target) {
      (event.target as HTMLDivElement).click();
    }
  }
};
